import { formatSvrBillingAmountDisplay, tryNormalizeSvrBillingAmount } from "./svrBillingMoney";
import { svrInvoiceFingerprintsEqual } from "./svrInvoiceContentFingerprint";

export const SVR_INVOICE_WHATSAPP_TEMPLATE = "SVR_INVOICE" as const;

export const SVR_WHATSAPP_SUCCESS_STATUSES = ["sent", "delivered", "read"] as const;
export const SVR_WHATSAPP_DISPATCHED_STATUSES = ["submitted", "accepted", "sent", "delivered", "read"] as const;
export const SVR_WHATSAPP_IN_FLIGHT_STATUSES = ["queued", "sending", "submitted", "awaiting_response"] as const;
export const SVR_WHATSAPP_RETRYABLE_STATUSES = ["failed", "undelivered"] as const;

export type SvrWhatsAppStaffStatus =
  | "idle"
  | "sending"
  | "submitted"
  | "sent"
  | "delivered"
  | "read"
  | "awaiting_response"
  | "failed";

export type SvrInvoiceWhatsAppActionKind =
  | "none"
  | "unconfirmed"
  | "in_flight"
  | "initial"
  | "resend"
  | "revised"
  | "retry";

export type SvrInvoiceWhatsAppAttemptSummary = {
  id?: number | null;
  status?: string | null;
  fingerprint?: string | null;
  attemptType?: string | null;
  initiatedAt?: Date | string | null;
  initiatedBy?: string | null;
  recipient?: string | null;
  amountDisplay?: string | null;
  invoiceNumber?: string | null;
  legacyEvidence?: boolean | null;
};

export type SvrInvoiceWhatsAppEligibilityInput = {
  visitStatus?: string | null;
  invoiceStatus?: string | null;
  invoiceUrl?: string | null;
  invoiceToken?: string | null;
  invoiceNumber?: string | null;
  rate?: unknown;
  customerWhatsApp?: string | null;
  whatsappSendStatus?: string | null;
  /** Authoritative Finance confirmation (`site_visit_billing.confirmedAt`). */
  confirmedAt?: Date | string | null;
  confirmedFingerprint?: string | null;
  currentFingerprint?: string | null;
  lastSuccessfulFingerprint?: string | null;
  latestAttempt?: SvrInvoiceWhatsAppAttemptSummary | null;
  lastSuccessfulAttempt?: SvrInvoiceWhatsAppAttemptSummary | null;
  isRegenerating?: boolean;
};

export type SvrInvoiceWhatsAppEligibility = {
  eligible: boolean;
  blockedReason: string | null;
  amount: number | null;
  amountDisplay: string | null;
  hasWhatsAppNumber: boolean;
  canRetry: boolean;
  alreadyDispatched: boolean;
  staffStatus: SvrWhatsAppStaffStatus;
  actionKind: SvrInvoiceWhatsAppActionKind;
  buttonLabel: string | null;
  unconfirmedMessage: string | null;
};

export function hasValidCustomerWhatsAppNumber(raw: string | null | undefined): boolean {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length === 8) return true;
  if (digits.length >= 10 && digits.length <= 15) return true;
  return false;
}

export function mapSvrWhatsAppStaffStatus(raw: string | null | undefined): SvrWhatsAppStaffStatus {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "queued" || s === "sending") return "sending";
  if (s === "accepted" || s === "submitted") return "submitted";
  if (s === "sent") return "sent";
  if (s === "delivered") return "delivered";
  if (s === "read") return "read";
  if (s === "awaiting_response") return "awaiting_response";
  if (s === "failed" || s === "undelivered" || s === "canceled" || s === "cancelled") return "failed";
  return "idle";
}

export function staffSvrWhatsAppLabel(status: SvrWhatsAppStaffStatus): string {
  switch (status) {
    case "sending":
      return "Sending";
    case "submitted":
      return "Submitted";
    case "sent":
      return "Sent";
    case "delivered":
      return "Delivered";
    case "read":
      return "Read";
    case "awaiting_response":
      return "Awaiting customer response";
    case "failed":
      return "Failed";
    default:
      return "Ready to send";
  }
}

export function isSvrInvoiceAuthoritativelyConfirmed(confirmedAt: Date | string | null | undefined): boolean {
  if (confirmedAt == null) return false;
  if (confirmedAt instanceof Date) return !Number.isNaN(confirmedAt.getTime());
  return String(confirmedAt).trim().length > 0;
}

export function svrInvoiceWhatsAppButtonLabel(kind: SvrInvoiceWhatsAppActionKind): string | null {
  switch (kind) {
    case "initial":
      return "SEND INVOICE VIA WHATSAPP";
    case "resend":
      return "RESEND INVOICE VIA WHATSAPP";
    case "revised":
      return "SEND REVISED INVOICE VIA WHATSAPP";
    case "retry":
      return "RETRY SENDING VIA WHATSAPP";
    default:
      return null;
  }
}

function statusOf(attempt: SvrInvoiceWhatsAppAttemptSummary | null | undefined, fallback?: string | null): string {
  return String(attempt?.status ?? fallback ?? "").trim().toLowerCase();
}

export function evaluateSvrInvoiceWhatsAppEligibility(
  input: SvrInvoiceWhatsAppEligibilityInput
): SvrInvoiceWhatsAppEligibility {
  const visitStatus = String(input.visitStatus ?? "").toLowerCase();
  const invoiceStatus = String(input.invoiceStatus ?? "").toLowerCase();
  const latestStatus = statusOf(input.latestAttempt, input.whatsappSendStatus);
  const hasWhatsAppNumber = hasValidCustomerWhatsAppNumber(input.customerWhatsApp);
  const amount = tryNormalizeSvrBillingAmount(input.rate);
  const amountDisplay = amount != null ? formatSvrBillingAmountDisplay(amount) : null;
  const staffStatus = mapSvrWhatsAppStaffStatus(latestStatus);
  const currentFp = String(input.currentFingerprint ?? "").trim();
  const confirmedFp = String(input.confirmedFingerprint ?? "").trim();
  const lastSuccessFp = String(
    input.lastSuccessfulAttempt?.fingerprint ?? input.lastSuccessfulFingerprint ?? "",
  ).trim();
  const alreadyDispatched =
    SVR_WHATSAPP_DISPATCHED_STATUSES.includes(latestStatus as (typeof SVR_WHATSAPP_DISPATCHED_STATUSES)[number]) ||
    Boolean(lastSuccessFp);
  const inFlight = SVR_WHATSAPP_IN_FLIGHT_STATUSES.includes(
    latestStatus as (typeof SVR_WHATSAPP_IN_FLIGHT_STATUSES)[number],
  );

  const result = (
    partial: Partial<SvrInvoiceWhatsAppEligibility> & Pick<SvrInvoiceWhatsAppEligibility, "actionKind">,
  ): SvrInvoiceWhatsAppEligibility => {
    const actionKind = partial.actionKind;
    const eligible = actionKind === "initial" || actionKind === "resend" || actionKind === "revised" || actionKind === "retry";
    return {
      eligible,
      blockedReason: partial.blockedReason ?? (eligible ? null : "This invoice cannot be sent via WhatsApp."),
      amount,
      amountDisplay,
      hasWhatsAppNumber,
      canRetry: actionKind === "retry",
      alreadyDispatched,
      staffStatus: partial.staffStatus ?? staffStatus,
      actionKind,
      buttonLabel: svrInvoiceWhatsAppButtonLabel(actionKind),
      unconfirmedMessage:
        actionKind === "unconfirmed"
          ? "Review and confirm this invoice before sending it to the customer."
          : null,
    };
  };

  if (visitStatus === "cancelled" || visitStatus === "canceled") {
    return result({ actionKind: "none", blockedReason: "This visit is cancelled. The invoice cannot be sent." });
  }
  if (invoiceStatus === "waived") {
    return result({ actionKind: "none", blockedReason: "This invoice has been waived and cannot be sent." });
  }
  if (invoiceStatus === "voided" || invoiceStatus === "cancelled" || invoiceStatus === "canceled" || invoiceStatus === "superseded") {
    return result({ actionKind: "none", blockedReason: "This invoice is no longer valid and cannot be sent." });
  }
  if (!input.invoiceNumber || !String(input.invoiceUrl ?? "").trim() || !String(input.invoiceToken ?? "").trim()) {
    return result({ actionKind: "none", blockedReason: "Generate a valid invoice before sending via WhatsApp." });
  }
  if (invoiceStatus !== "raised" && invoiceStatus !== "outstanding" && invoiceStatus !== "paid") {
    return result({ actionKind: "none", blockedReason: "Generate a valid invoice before sending via WhatsApp." });
  }
  if (amount == null) {
    return result({ actionKind: "none", blockedReason: "The authoritative invoice amount is missing." });
  }
  if (!hasWhatsAppNumber) {
    return result({ actionKind: "none", blockedReason: "Customer has no valid WhatsApp number on record." });
  }
  if (input.isRegenerating) {
    return result({
      actionKind: "unconfirmed",
      blockedReason: "This invoice is being revised. Review and confirm the revised invoice before sending.",
    });
  }

  const confirmationBindsToCurrent =
    isSvrInvoiceAuthoritativelyConfirmed(input.confirmedAt) &&
    (!currentFp || !confirmedFp || svrInvoiceFingerprintsEqual(currentFp, confirmedFp));
  if (!confirmationBindsToCurrent) {
    return result({
      actionKind: "unconfirmed",
      blockedReason: "Review and confirm this invoice before sending it to the customer.",
    });
  }

  if (inFlight) {
    return result({
      actionKind: "in_flight",
      blockedReason: "A WhatsApp send is already in progress.",
      staffStatus: staffStatus === "idle" ? "sending" : staffStatus,
    });
  }

  const latestFailed = SVR_WHATSAPP_RETRYABLE_STATUSES.includes(
    latestStatus as (typeof SVR_WHATSAPP_RETRYABLE_STATUSES)[number],
  );
  const latestFp = String(input.latestAttempt?.fingerprint ?? "").trim();
  if (latestFailed && (!latestFp || !currentFp || svrInvoiceFingerprintsEqual(latestFp, currentFp))) {
    return result({ actionKind: "retry", blockedReason: null });
  }

  const lastSuccess = lastSuccessFp || String(input.lastSuccessfulAttempt?.fingerprint ?? "").trim();
  if (lastSuccess) {
    if (svrInvoiceFingerprintsEqual(lastSuccess, currentFp) && !String(lastSuccess).startsWith("legacy:")) {
      return result({ actionKind: "resend", blockedReason: null });
    }
    return result({ actionKind: "revised", blockedReason: null });
  }

  if (alreadyDispatched && !lastSuccess) {
    return result({ actionKind: "revised", blockedReason: null });
  }

  return result({ actionKind: "initial", blockedReason: null });
}

export type SvrInvoiceWhatsAppConfirmationMode = "initial" | "resend" | "revised" | "retry";

/** Must mirror clarity_svr_invoice_v2 in server/whatsapp.ts. */
export function renderSvrInvoiceWhatsAppMessage(params: {
  customerName: string;
  invoiceUrl: string;
  picName: string;
  picPhone: string;
}): string {
  return `Hi ${params.customerName}, please see the invoice for your action: ${params.invoiceUrl}\nPlease contact ${params.picName} at ${params.picPhone} if you have any questions.\nClarity Group`;
}

/** Must mirror the approved proposal_payment_reminder Twilio template. */
export function renderSvrInvoiceReminderWhatsAppMessage(params: {
  customerName: string;
  invoiceUrl: string;
}): string {
  return `Dear ${params.customerName},\n\nYou have an invoice waiting for your action.\n\nYou may complete your payment here:\n\n${params.invoiceUrl}\n\nIf you have already made payment, please disregard this message.\n\nThank you.\n\nCleo  \n\nfor the Clarity Group`;
}

export function buildSvrInvoiceWhatsAppConfirmation(params: {
  customerName: string;
  customerWhatsApp: string;
  invoiceNumber: string;
  amount: unknown;
  mode?: SvrInvoiceWhatsAppConfirmationMode;
  paymentLinkDestination?: string | null;
  lastSentAt?: Date | string | null;
  lastSentBy?: string | null;
  previousAmountDisplay?: string | null;
  currentAmountDisplay?: string | null;
  picName?: string | null;
  picPhone?: string | null;
}): {
  title: string;
  finalAction: string;
  customerName: string;
  customerWhatsApp: string;
  invoiceNumber: string;
  amountDisplay: string;
  amountDollar: string;
  statement: string;
  paymentLinkDestination: string | null;
  lastSentLabel: string | null;
  previousAmountDisplay: string | null;
  currentAmountDisplay: string | null;
  messageBody: string;
} {
  const amountDisplay = formatSvrBillingAmountDisplay(params.amount);
  const mode = params.mode ?? "initial";
  const lastSent =
    params.lastSentAt != null && String(params.lastSentAt).trim()
      ? `${params.lastSentBy ? `${params.lastSentBy} · ` : ""}${new Date(params.lastSentAt).toLocaleString("en-SG", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })}`
      : params.lastSentBy ?? null;
  const messageBody = renderSvrInvoiceWhatsAppMessage({
    customerName: params.customerName,
    invoiceUrl: params.paymentLinkDestination ?? "[invoice link unavailable]",
    picName: params.picName ?? "Clarity",
    picPhone: params.picPhone ?? "+65 6235 9555",
  });

  if (mode === "resend") {
    return {
      title: "Resend invoice via WhatsApp?",
      finalAction: "RESEND NOW",
      customerName: params.customerName,
      customerWhatsApp: params.customerWhatsApp,
      invoiceNumber: params.invoiceNumber,
      amountDisplay,
      amountDollar: `$${amountDisplay}`,
      statement: "The customer has already received this invoice.",
      paymentLinkDestination: params.paymentLinkDestination ?? null,
      lastSentLabel: lastSent,
      previousAmountDisplay: null,
      currentAmountDisplay: amountDisplay,
      messageBody,
    };
  }
  if (mode === "revised") {
    return {
      title: "Send revised invoice via WhatsApp?",
      finalAction: "SEND REVISED INVOICE",
      customerName: params.customerName,
      customerWhatsApp: params.customerWhatsApp,
      invoiceNumber: params.invoiceNumber,
      amountDisplay,
      amountDollar: `$${amountDisplay}`,
      statement: "A previous version was sent. This will send the revised invoice.",
      paymentLinkDestination: params.paymentLinkDestination ?? null,
      lastSentLabel: lastSent,
      previousAmountDisplay: params.previousAmountDisplay ?? null,
      currentAmountDisplay: params.currentAmountDisplay ?? amountDisplay,
      messageBody,
    };
  }
  return {
    title: "Send invoice via WhatsApp?",
    finalAction: "SEND NOW",
    customerName: params.customerName,
    customerWhatsApp: params.customerWhatsApp,
    invoiceNumber: params.invoiceNumber,
    amountDisplay,
    amountDollar: `$${amountDisplay}`,
    statement: "The invoice and payment link will be sent through WhatsApp using the approved SVR invoice template.",
    paymentLinkDestination: params.paymentLinkDestination ?? null,
    lastSentLabel: lastSent,
    previousAmountDisplay: null,
    currentAmountDisplay: amountDisplay,
    messageBody,
  };
}
